import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/browser";
import { cognitoConfig, getCognitoLogoutUrl } from "Config/cognito";
import API from "Utils/api";
import { sendSentryEvent, setSentryUserFromStorage } from "Utils/sentryUtils";
import {
  AUTH_STORAGE_KEYS,
  MAX_COGNITO_RETRIES,
  clearAuthTokens,
  getCognitoRetryCount,
  incrementCognitoRetry,
  resetCognitoRetry,
} from "Utils/cognito";

import {
  CallbackContainerStyled,
  CallbackTitleStyled,
  CallbackMessageStyled,
  CallbackButtonStyled,
} from "./styles";

export const CognitoCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Cognito stores its session in cookies on its own domain
    // (e.g. .auth-dev.cyngn.com), which we can't clear from JS on this origin.
    // When the oauth2/token exchange or backend validation throws — the
    // typical stale-cookie failure mode — bounce through Cognito's /logout
    // endpoint to clear those cookies server-side, then auto-restart SSO via
    // /login. We deliberately do NOT apply this to ?error= redirects: those
    // represent Cognito's own decisions (access_denied = user cancel,
    // server_error, invalid_request) and should surface, not silently retry.
    const recoverFromStaleSession = (displayMessage: string, err?: unknown) => {
      if (getCognitoRetryCount() < MAX_COGNITO_RETRIES) {
        const attempt = getCognitoRetryCount() + 1;
        console.warn(
          "[Cognito] SSO callback failed; clearing stale session via /logout (attempt %d/%d)",
          attempt,
          MAX_COGNITO_RETRIES
        );
        Sentry.addBreadcrumb({
          category: "auth",
          level: "warning",
          message: "Cognito stale-session recovery triggered",
          data: { attempt, max: MAX_COGNITO_RETRIES },
        });
        incrementCognitoRetry();
        clearAuthTokens();
        window.location.href = getCognitoLogoutUrl();
        return;
      }
      console.warn(
        "[Cognito] SSO recovery exhausted after %d attempt(s); surfacing error to user",
        MAX_COGNITO_RETRIES
      );
      if (err) {
        const errorForSentry =
          err instanceof Error ? err : new Error(String(err));
        sendSentryEvent(errorForSentry, "Cognito.RecoveryExhausted");
      }
      resetCognitoRetry();
      setError(displayMessage);
    };

    const handleCallback = async () => {
      const code = searchParams.get("code");
      const errorParam = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      if (errorParam) {
        console.warn(
          "[Cognito] SSO callback error:",
          errorParam,
          errorDescription
        );
        resetCognitoRetry();
        setError(errorDescription || errorParam);
        return;
      }

      if (!code) {
        console.warn("[Cognito] SSO callback: no authorization code in URL");
        resetCognitoRetry();
        setError("No authorization code received");
        return;
      }

      console.info(
        "[Cognito] SSO callback: exchanging authorization code for tokens"
      );

      try {
        // Exchange code for Cognito tokens
        const tokenResponse = await exchangeCodeForTokens(code);

        // Validate with backend and get tokens back (backend handles user provisioning)
        const loginResponse = await API.post("/login/cognito/", {
          id_token: tokenResponse.id_token,
          access_token: tokenResponse.access_token,
          refresh_token: tokenResponse.refresh_token,
          expires_in: tokenResponse.expires_in,
        });

        // Store Cognito tokens (not Django JWT)
        // Note: Backend uses camelCase due to DRF CamelCase renderer
        const { accessToken, idToken, refreshToken, expiresIn } =
          loginResponse.data;

        localStorage.setItem(AUTH_STORAGE_KEYS.JWT, `Bearer ${accessToken}`);
        localStorage.setItem(AUTH_STORAGE_KEYS.COGNITO_ID_TOKEN, idToken);
        localStorage.setItem(
          AUTH_STORAGE_KEYS.COGNITO_REFRESH_TOKEN,
          refreshToken
        );
        // Store expiry time for refresh logic
        const expiresAt = Date.now() + expiresIn * 1000;
        localStorage.setItem(
          AUTH_STORAGE_KEYS.COGNITO_EXPIRES_AT,
          expiresAt.toString()
        );

        // Set Sentry user
        setSentryUserFromStorage();

        console.info("[Cognito] SSO login complete, redirecting to app");
        resetCognitoRetry();
        // Redirect to app
        navigate("/");
      } catch (err) {
        console.error("[Cognito] SSO login failed:", err);
        recoverFromStaleSession(
          "Authentication failed. Please try again.",
          err
        );
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <CallbackContainerStyled>
        <CallbackTitleStyled>Login Failed</CallbackTitleStyled>
        <CallbackMessageStyled>{error}</CallbackMessageStyled>
        <CallbackButtonStyled onClick={() => navigate("/login")}>
          Back to Login
        </CallbackButtonStyled>
      </CallbackContainerStyled>
    );
  }

  return (
    <CallbackContainerStyled>
      <CallbackMessageStyled>Completing sign in...</CallbackMessageStyled>
    </CallbackContainerStyled>
  );
};

async function exchangeCodeForTokens(code: string) {
  const { domain, clientId, redirectSignIn } = cognitoConfig;

  const response = await fetch(`https://${domain}/oauth2/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId,
      code,
      redirect_uri: redirectSignIn,
    }),
  });

  if (!response.ok) {
    console.error("[Cognito] Code exchange failed: status=%s", response.status);
    throw new Error("Failed to exchange code for tokens");
  }

  console.info("[Cognito] Code exchange successful");
  return response.json();
}
